import keys from '@debbie/common/dist/cache/keys.js';
import { isAdmin } from '@debbie/common/dist/permissions/index.js';
import { filterPropertyTypes, } from '@debbie/common/dist/properties/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { objectBy } from '@debbie/common/dist/util/index.js';
import { properties as propertiesAPI } from '@debbie/cortex/dist/api/index.js';
import { useCaseGroups } from '@debbie/cortex/dist/case-groups/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useRole } from '@debbie/cortex/dist/me/index.js';
import { propertiesKey, propertyTypesEntity, useProperties, usePropertyTypes, } from '@debbie/cortex/dist/properties/index.js';
import q from '@debbie/cortex/dist/query.js';
import React from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { errorNotification } from '../../utils/errors.js';
import CaseGroup from '../CaseGroup.js';
import DraggableList from '../DraggableList.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import PropertyCmp from './Property.js';
const Properties = (props) => {
    const lang = useSelector((s) => s.lang);
    const meRole = useRole();
    const propertyTypesLoadable = usePropertyTypes();
    const propertiesLoadable = useProperties(props.propRef, props.propRefId);
    const properties = h.hasData(propertiesLoadable)
        ? propertiesLoadable.data
        : null;
    const loading = h.isLoading(propertyTypesLoadable) || h.isLoading(propertiesLoadable);
    const [orderIndex, setOrderIndex] = React.useState({});
    const editable = props.editable !== false;
    const updateProperty = async (prop) => {
        const updatedProperty = await propertiesAPI.update({
            handle: prop.handle,
            ref: props.propRef,
            refId: props.propRefId,
            data: prop.data,
            type: prop.type,
        });
        q.invalidate([propertiesKey(props.propRef, props.propRefId)]);
        return updatedProperty;
    };
    const saveOrdering = useMutation((ordering) => propertiesAPI.setOrdering(ordering), {
        onSuccess: () => {
            q.invalidate(keys.write.create(propertyTypesEntity));
        },
        onError: (err) => errorNotification(lang, err, 'update', lang.errorEntities.property),
    });
    const propertyTypesGrouped = h.hasData(propertyTypesLoadable)
        ? groupPropertyTypes(propertyTypesLoadable.data, props.propRef, props.caseGroupId)
        : null;
    const settingsReadOnly = !(meRole && isAdmin(meRole));
    return (React.createElement(PropertiesStyled, null,
        loading && React.createElement(PlaceholderLoader, null),
        propertyTypesGrouped ? (propertyTypesGrouped.map(({ propertyTypes, caseGroupId }, index) => (React.createElement("div", { key: caseGroupId ?? 'all' },
            index !== 0 && React.createElement("hr", null),
            React.createElement(PropertyScopeHeader, { caseGroupId: caseGroupId }),
            React.createElement(DraggableList, { list: propertyTypes
                    .sort((a, b) => {
                    const indexA = typeof orderIndex[a.propertyTypeId] === 'number'
                        ? orderIndex[a.propertyTypeId]
                        : a.orderIndex || 0;
                    const indexB = typeof orderIndex[b.propertyTypeId] === 'number'
                        ? orderIndex[b.propertyTypeId]
                        : b.orderIndex || 0;
                    return indexA - indexB;
                })
                    .map((propertyType) => {
                    const property = properties?.find((p) => p.handle === propertyType.handle);
                    return {
                        id: propertyType.propertyTypeId,
                        content: (React.createElement("div", { style: {
                                color: THEME.COLOR_PRIMARY,
                                fontWeight: 'bold',
                                fontSize: '12px',
                                textTransform: 'uppercase',
                            } },
                            React.createElement(PropertyCmp, { draggable: !settingsReadOnly, editable: editable, key: propertyType.propertyTypeId, propertyType: propertyType, property: property, updateProperty: ({ data }) => updateProperty({
                                    data,
                                    type: propertyType.type,
                                    handle: propertyType.handle,
                                }), refId: props.propRefId }))),
                    };
                }), onChange: (items) => {
                    const newIndex = items.map(({ id }, index) => ({
                        index,
                        propertyTypeId: id,
                    }));
                    setOrderIndex({
                        ...orderIndex,
                        ...newIndex.reduce((acc, curr) => {
                            acc[curr.propertyTypeId] = curr.index;
                            return acc;
                        }, {}),
                    });
                    saveOrdering.mutate(newIndex);
                }, readOnly: settingsReadOnly }))))) : (React.createElement(PlaceholderLoader, null))));
};
export default Properties;
const PropertiesStyled = styled.div `
  font-size: 0.75em;
  input,
  select {
    margin-bottom: 0px;
  }
`;
export const PropertyScopeHeader = (props) => {
    const caseGroupsLoadable = useCaseGroups();
    const caseGroups = h.hasData(caseGroupsLoadable)
        ? caseGroupsLoadable.data
        : null;
    if (!caseGroups) {
        return null;
    }
    const caseGroupsMap = objectBy(caseGroups, (cG) => cG.id);
    const caseGroup = props.caseGroupId ? caseGroupsMap[props.caseGroupId] : null;
    if (caseGroup) {
        return (React.createElement("div", { style: { fontSize: '1.5em', marginBottom: '0.6em' } },
            React.createElement(CaseGroup, { caseGroup: caseGroup })));
    }
    return null;
};
/**
 * The types reachable from `caseGroupId`, in two blocks: the ones that apply to
 * every case, and the ones this case group brings along. A type scoped to
 * several case groups shows up under the one being viewed.
 */
export const groupPropertyTypes = (propertyTypes, ref, caseGroupId) => {
    const reachable = filterPropertyTypes(propertyTypes, ref, caseGroupId);
    return [
        {
            caseGroupId: null,
            propertyTypes: reachable.filter((type) => type.scopes.length === 0),
        },
        {
            caseGroupId,
            propertyTypes: reachable.filter((type) => type.scopes.length > 0),
        },
    ]
        .filter((group) => group.propertyTypes.length > 0)
        .map((group) => ({
        ...group,
        propertyTypes: group.propertyTypes.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)),
    }));
};
//# sourceMappingURL=Properties.js.map